
import React, { useState, useRef, useMemo, useId } from 'react';

interface RateChartProps {
  data: number[];
  labels: string[];
  lineColor?: string;
  areaColor?: string;
}

export const RateChart: React.FC<RateChartProps> = ({ 
  data, 
  labels,
  lineColor = '#4A5568', 
  areaColor = 'rgba(74, 85, 104, 0.1)' 
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const [tooltip, setTooltip] = useState<{ x: number; y: number; label: string; value: number } | null>(null);

  const viewBox = { width: 500, height: 200 };
  const padding = { top: 10, right: 10, bottom: 20, left: 30 };

  // Generate a unique ID for this chart instance's gradient
  const uniqueId = useId();
  // useId returns strings with colons which are invalid in CSS selectors without escaping
  // We sanitize it to be safe for ID usage
  const gradientId = `areaGradient-${uniqueId.replace(/[^a-zA-Z0-9]/g, '')}`;

  const { points, path, areaPath, yAxisLabels } = useMemo(() => {
    const min = Math.min(...data);
    const max = Math.max(...data);
    const yRange = max - min;
    const yAxisBuffer = yRange * 0.1; // Add 10% buffer
    const yMin = Math.max(0, min - yAxisBuffer);
    const yMax = max + yAxisBuffer;

    const effectiveWidth = viewBox.width - padding.left - padding.right;
    const effectiveHeight = viewBox.height - padding.top - padding.bottom;

    const points = data.map((d, i) => {
      const x = padding.left + (i / (data.length - 1)) * effectiveWidth;
      const y = padding.top + effectiveHeight - ((d - yMin) / (yMax - yMin)) * effectiveHeight;
      return { x, y };
    });

    const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x} ${p.y}`).join(' ');
    
    const areaPath = `${path} V ${viewBox.height - padding.bottom} H ${padding.left} Z`;

    const yAxisLabels = [
        { value: min.toFixed(2), y: padding.top + effectiveHeight - ((min - yMin) / (yMax - yMin)) * effectiveHeight },
        { value: max.toFixed(2), y: padding.top + effectiveHeight - ((max - yMin) / (yMax - yMin)) * effectiveHeight },
    ];

    return { points, path, areaPath, yAxisLabels };
  }, [data]);
  
  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    
    const ctm = svg.getScreenCTM();
    if (!ctm) return;

    const { x } = pt.matrixTransform(ctm.inverse());
    
    const index = Math.round(((x - padding.left) / (viewBox.width - padding.left - padding.right)) * (data.length - 1));

    if (index >= 0 && index < data.length) {
      const point = points[index];
      setTooltip({
        x: point.x,
        y: point.y,
        label: labels[index],
        value: data[index],
      });
    }
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  return (
    <div className="relative w-full h-full">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${viewBox.width} ${viewBox.height}`}
        className="w-full h-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {/* Y-Axis Grid Lines & Labels */}
        <line x1={padding.left} y1={0} x2={padding.left} y2={viewBox.height - padding.bottom} stroke="#e2e8f0" strokeWidth="1" className="stroke-gray-200 dark:stroke-gray-600" />
        {yAxisLabels.map(({ value, y }) => (
            <g key={value}>
                <text x={padding.left - 5} y={y} fontSize="10" textAnchor="end" alignmentBaseline="middle" className="fill-gray-500 dark:fill-gray-400">{value}%</text>
            </g>
        ))}

        {/* X-Axis Labels */}
        <text x={padding.left} y={viewBox.height - 5} fontSize="10" textAnchor="start" className="fill-gray-500 dark:fill-gray-400">{labels[0]}</text>
        <text x={viewBox.width - padding.right} y={viewBox.height - 5} fontSize="10" textAnchor="end" className="fill-gray-500 dark:fill-gray-400">{labels[labels.length - 1]}</text>
        
        {/* Area Gradient */}
        <defs>
            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={areaColor} stopOpacity={0.8} />
                <stop offset="100%" stopColor={areaColor} stopOpacity={0} />
            </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradientId})`} />

        {/* Line */}
        <path d={path} fill="none" stroke={lineColor} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />

        {/* Tooltip */}
        {tooltip && (
          <g>
            <line x1={tooltip.x} y1={padding.top} x2={tooltip.x} y2={viewBox.height - padding.bottom} stroke="#a0aec0" strokeDasharray="3,3" />
            <circle cx={tooltip.x} cy={tooltip.y} r="4" fill={lineColor} stroke="white" strokeWidth="2" />
          </g>
        )}
      </svg>
      {tooltip && (
         <div 
            className="absolute p-2 text-xs bg-gray-800 text-white rounded-md shadow-lg pointer-events-none transition-opacity"
            style={{
                left: `${(tooltip.x / viewBox.width) * 100}%`,
                top: `${(tooltip.y / viewBox.height) * 100}%`,
                transform: `translate(-50%, -120%)`,
                opacity: 1,
            }}
        >
            <div className="font-semibold">{tooltip.label}</div>
            <div>Rate: {tooltip.value.toFixed(3)}%</div>
        </div>
      )}
    </div>
  );
};
